import React, { useEffect } from 'react';
import { Translation, getI18n } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import Logger from '../../../../../lib/Logger';
import { Table, Checkbox } from 'antd';
import {
  LearningTypeTag,
  PublicStatusTag,
} from '../../../elements/components/Tags';
import Format from '../../../../../lib/Format';
import { pathTo } from '../../../Routes';
import QueryString from '../../../../../lib/QueryString';
import notification from '../../../elements/lib/NotificationWrapper';
import LearningAdminModuleSeriesTableListHeader from './LearningAdminModuleSeriesTableListHeader';
import LearningAdminModuleSeriesListAction from './LearningAdminModuleSeriesListAction';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';

const LearningAdminModuleSeriesTableList = ({
  list,
  order,
  page,
  action,
  value,
  total,
  limit,
  load,
  isLoadingIds,
  partnershipId,
  setShowList,
  setGlobalTotal,
  selectedRowKeys,
  updateSelectedRowKeys,
  loadIds,
  ids,
  filter,
  component,
  history,
  remove,
  removes,
  view,
  isLoading,
  activeKey,
  trackerActions,
  ...props
}) => {
  const filterString = JSON.stringify(filter);

  useEffect(() => {
    load(partnershipId, action, JSON.parse(filterString), page, limit, order);
  }, [partnershipId, action, load, page, order, filterString, limit]);

  useEffect(() => {
    loadIds(partnershipId, JSON.parse(filterString), order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, filterString, loadIds]);

  useEffect(() => {
    setShowList(list.length);
    setGlobalTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, list]);

  const handleTableChange = (pagination, filters, sorter) => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.LEARNINGS_ADMIN_SEARCH_MODULE_SERIE,
      {
        sorterField: (Array.isArray(sorter) ? sorter[0] : sorter)?.field,
        sorterOrder: (Array.isArray(sorter) ? sorter[0] : sorter)?.order,
        ...Object.fromEntries(
          Object.entries(filters || {})
            .filter(([k, v]) => Array.isArray(v) && v.length > 0)
            .map(([k, v]) => [k, v.join(',')])
        ),
        paginationCurrent: pagination['current'],
      }
    );
    let path = props.location.pathname;
    const paramsOld = QueryString.parse(props.location.search);
    const view = paramsOld.view;
    const params = {};

    // handle pagination
    if ('current' in pagination && pagination['current']) {
      path = pathTo(component, { page: pagination['current'] });
    }

    // handle filters
    if (filters) {
      for (const key in filters) {
        if (filters[key]) {
          params[key] = filters[key].join(',');
        }
      }
    }

    // handle view
    if (view) {
      params['view'] = view;
    }
    history.push(QueryString.append(path, params));
  };

  const pagination = {
    current: page,
    pageSize: limit,
    total: total,
    showSizeChanger: false,
    position: ['bottomCenter'],
  };

  const onSelectChange = (record, selected) => {
    updateSelectedRowKeys(record.key, selected, false);
  };

  const toggleSelectAll = (e) => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER
        .LEARNINGS_ADMIN_TOGGLE_SELECT_ALL_MODULE_SERIE
    );
    if (e.target.checked) {
      const selectableKeys = list
        .filter((item) => item.public_status === 3)
        .map((item) => item.key);

      updateSelectedRowKeys(selectableKeys, false, true);
    } else {
      updateSelectedRowKeys([], false, true);
    }
  };

  const selectInvertAll = (selectAllData) => {
    if (ids.length > 0 && selectAllData) {
      const selectableIds = ids.filter((id) => {
        const item = list.find((record) => record.key === id);
        return item && item.public_status !== 1;
      });

      updateSelectedRowKeys(selectableIds, false, true);
    } else {
      updateSelectedRowKeys([], false, true);
    }
  };
  const handleDelete = (id) => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.LEARNINGS_ADMIN_DELETE_MODULE_SERIE
    );
    remove(partnershipId, id, (success) => {
      if (success) {
        selectInvertAll(false);
        load(partnershipId, action, filter, page, limit, order);
        notification.success(getI18n().t('feedback_delete_success'));
      } else {
        notification.error(getI18n().t('feedback_delete_error'));
      }
    });
  };

  const handleDeletes = () => {
    trackerActions(
      mixpanelEventsEnum.ACTIONS_PARTNER.LEARNINGS_ADMIN_DELETES_MODULE_SERIES
    );
    removes(
      partnershipId,
      { module_series_ids: selectedRowKeys },
      (success) => {
        if (success) {
          const params = QueryString.parse(history.location.search);
          const path = pathTo(component, {
            page: 1,
            view: view,
          });
          history.push(QueryString.append(path, params));
          selectInvertAll(false);
          load(partnershipId, action, filter, page, limit, order);
          notification.success(getI18n().t('feedback_delete_success'));
        } else {
          notification.error(getI18n().t('feedback_delete_error'));
        }
      }
    );
  };

  const headerCheckbox = (
    <Checkbox
      checked={selectedRowKeys.length}
      indeterminate={
        selectedRowKeys.length > 0 && selectedRowKeys.length < total
      }
      onChange={toggleSelectAll}
    />
  );

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    fixed: true,
    onSelect: onSelectChange,
    columnTitle: headerCheckbox,
    getCheckboxProps: (record) => ({
      disabled: record.public_status !== 3,
    }),
  };

  const columns = [
    {
      title: getI18n().t('learning_module_serie_list_table_title'),
      key: 'title',
      dataIndex: 'title',
      render: (title) => <>{title}</>,
    },
    {
      title: getI18n().t('learning_module_serie_list_table_type'),
      dataIndex: 'is_recommended',
      key: 'is_recommended',
      filters: [
        { text: getI18n().t('learning_type_recommended'), value: true },
        { text: getI18n().t('learning_type_not_recommended'), value: false },
      ],
      render: (is_recommended) => <LearningTypeTag value={is_recommended} />,
    },
    {
      title: getI18n().t('learning_module_serie_list_table_public_status'),
      dataIndex: 'public_status',
      key: 'public_status',
      filters: [
        { text: getI18n().t('learning_public_status_published'), value: 1 },
        { text: getI18n().t('learning_public_status_draft'), value: 2 },
        { text: getI18n().t('learning_public_status_archived'), value: 3 },
      ],
      render: (public_status) => <PublicStatusTag value={public_status} />,
    },
    {
      title: getI18n().t('learning_module_serie_list_table_order'),
      dataIndex: 'order',
      key: 'order',
    },
    {
      title: getI18n().t('table_header_created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => Format.dateTable(date),
    },

    {
      title: getI18n().t('table_header_action'),
      key: 'actions',
      width: 77,
      fixed: 'right',
      render: (row) => (
        <>
          <LearningAdminModuleSeriesListAction
            row={row}
            partnershipId={partnershipId}
            editScreen='LearningAdminModuleSerieEditScreen'
            handleDelete={() => handleDelete(row.id)}
            view={view}
            activeKey={activeKey}
          />
        </>
      ),
    },
  ];

  return (
    <Translation>
      {(t) => (
        <>
          <LearningAdminModuleSeriesTableListHeader
            selectedRowKeys={selectedRowKeys}
            handleDeletes={() => handleDeletes()}
          />
          <Table
            className='module-serie-list-table'
            dataSource={list}
            columns={columns}
            pagination={page === 1 && total <= limit ? false : pagination}
            loading={isLoading}
            scroll={{ x: true }}
            onChange={handleTableChange}
            rowSelection={!isLoadingIds && rowSelection}
          />
        </>
      )}
    </Translation>
  );
};

export default withRouter(LearningAdminModuleSeriesTableList);

Logger.log('silly', `LearningAdminModuleSeriesTableList loaded.`);
